import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Import SVG icons directly from the image-card-icons directory
import { ReactComponent as CardAdd } from '../images/icons/image-card-icons/card-icon-add-to-collection.svg';
import { ReactComponent as CardInCollection } from '../images/icons/image-card-icons/card-icon-add-to-collection-active.svg';
import { ReactComponent as CardRemove } from '../images/icons/image-card-icons/card-icon-remove.svg';
import { ReactComponent as CardCopy } from '../images/icons/image-card-icons/card-icon-copy-image.svg';
import { ReactComponent as CardSave } from '../images/icons/image-card-icons/card-icon-save-image.svg';
import { ReactComponent as CardZoom } from '../images/icons/image-card-icons/card-icon-zoom-in.svg';
import { ReactComponent as CardTag } from '../images/icons/image-card-icons/card-icon-tag.svg';
import { ReactComponent as CardEye } from '../images/icons/image-card-icons/card-icon-eye.svg';
import { ReactComponent as CardImageDescribe } from '../images/icons/image-card-icons/card-icon-describe-image.svg';
import { ReactComponent as CardEdit } from '../images/icons/image-card-icons/card-icon-edit.svg';
import { ReactComponent as UploadIconWhite } from "../images/icons/input-bar-icons/icon-input-bar-upload-white.svg";

// Get electron ipcRenderer for accessing file system
const ipcRenderer = window.require ? window.require('electron').ipcRenderer : null;

// MoMM Chat Icon Component (you can replace this with an actual icon if you have one)
const MOMMIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

/**
 * ComfyImageToolbar component
 * Displays hover tools above the image and ID at the bottom
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - The media item to operate on
 * @param {boolean} props.inCollection - Whether the item is in collection
 * @param {function} props.onToggleCollection - Function to toggle collection state
 * @param {function} props.onDelete - Function to delete the item
 * @param {function} props.onZoom - Function to zoom/view the image full screen
 * @param {boolean} props.isInMommPage - Whether this toolbar is being used in the MOMM page
 */
const ComfyImageToolbar = ({ 
  item, 
  inCollection, 
  onToggleCollection, 
  onDelete, 
  onZoom, 
  onUseImage,
  onSendToOtherApp,
  onCopyToClipboard,
  onSaveImage,
  onTagImage,
  onDescribeImage,
  isInMommPage = false
}) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [downloadName, setDownloadName] = useState(`Comfy_${item.hash ? item.hash.substring(0, 9) : 'image'}`);
  // Add local state for immediate visual feedback
  const [localInCollection, setLocalInCollection] = useState(inCollection);
  const navigate = useNavigate();

  // Update local state when prop changes
  React.useEffect(() => {
    setLocalInCollection(inCollection);
  }, [inCollection]);

  // Handle toggle collection
  const handleToggleCollection = (e) => {
    e.stopPropagation();
    // Update local state immediately for instant visual feedback
    setLocalInCollection(!localInCollection);
    console.log("handleToggleCollection in toolbar: ", item);
    if (onToggleCollection) {
      onToggleCollection(item, e);
    }
  };

  // Handle zoom/examine image
  const handleZoom = (e) => {
    e.stopPropagation();
    if (onZoom) {
      onZoom(item);
    }
  };

  // Handle showing image details
  const handleShowDetails = (e) => {
    e.stopPropagation();
    setShowDetailsDialog(true);
  };

  // Handle closing details dialog
  const closeDetailsDialog = (e) => {
    if (e) e.stopPropagation();
    setShowDetailsDialog(false);
  };

  // Handle copy to clipboard
  const copyImageToClipboard = async (e) => {
    e.stopPropagation();
    if (onCopyToClipboard) {
      onCopyToClipboard(item);
    } else {
      try {
        if (item.image) {
          const blob = await fetch(`data:image/png;base64,${item.image}`).then(r => r.blob());
          await navigator.clipboard.write([
            new ClipboardItem({
              [blob.type]: blob
            })
          ]);
        }
      } catch (err) {
        console.error('Failed to copy image: ', err);
      }
    }
  };

  // Handle save image
  const saveImage = async (e) => {
    e.stopPropagation();
    if (onSaveImage) {
      onSaveImage(item, downloadName);
    } else {
      try {
        if (item.image) {
          const link = document.createElement('a');
          link.href = `data:image/png;base64,${item.image}`;
          link.download = downloadName || `image-${item.hash}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error('Failed to save image: ', err);
      }
    }
  };

  // Handle using image
  const handleUseImage = (e) => {
    e.stopPropagation();
    if (onUseImage) {
      console.log("ComfyImageToolbar - handleUseImage called for item:", item.hash);
      
      try {
        // Check if we have a valid item
        if (!item) {
          console.error("No valid item provided to handleUseImage");
          return;
        }
        
        // Try to get the image data from the item, handling different formats
        let imageData = item.image;
        
        // If imageData is a Blob or ArrayBuffer, convert it to base64
        if (imageData instanceof Blob || imageData instanceof ArrayBuffer) {
          console.log("ComfyImageToolbar - Converting Blob/ArrayBuffer to base64");
          
          // Convert to base64 asynchronously
          const reader = new FileReader();
          reader.onloadend = function() {
            const base64data = reader.result;
            // Now call onUseImage with the base64 data
            onUseImage(base64data, item.prompt);
          };
          
          if (imageData instanceof ArrayBuffer) {
            reader.readAsDataURL(new Blob([imageData]));
          } else {
            reader.readAsDataURL(imageData);
          }
          return; // Exit early since we're handling this asynchronously
        }
        
        // Call the parent handler with the processed image data
        onUseImage(imageData, item.prompt);
      } catch (error) {
        console.error("Error in ComfyImageToolbar.handleUseImage:", error);
        
        // Show error notification
        const errorToast = document.createElement('div');
        errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        errorToast.textContent = `Error using image: ${error.message}`;
        document.body.appendChild(errorToast);
        
        setTimeout(() => {
          errorToast.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(errorToast)) {
              document.body.removeChild(errorToast);
            }
          }, 300);
        }, 2000);
      }
    }
  };

  // Handle delete
  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item.hash, e);
    }
  };

  // Handle send to MoMM
  const handleSendToMoMM = (e) => {
    e.stopPropagation();
    
    try {
      console.log('📤 ComfyImageToolbar: Starting send to MOMM...', {
        dataType: item.data_type,
        hasImage: !!item.image,
        imageLength: item.image ? item.image.length : 0,
        hash: item.hash
      });

      // Only process image type media
      if (item.data_type !== 'image') {
        console.warn('📤 ComfyImageToolbar: Only images can be sent to MoMM, got:', item.data_type);
        
        // Show user-friendly error
        const errorToast = document.createElement('div');
        errorToast.className = 'fixed top-4 right-4 bg-yellow-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        errorToast.textContent = 'Only images can be sent to MoMM';
        document.body.appendChild(errorToast);
        
        setTimeout(() => {
          errorToast.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(errorToast)) {
              document.body.removeChild(errorToast);
            }
          }, 300);
        }, 2000);
        return;
      }

      if (!item.image) {
        console.error('📤 ComfyImageToolbar: No image data available', item);
        throw new Error('No image data available');
      }

      // Validate base64 data
      if (typeof item.image !== 'string') {
        console.error('📤 ComfyImageToolbar: Invalid image data type:', typeof item.image);
        throw new Error('Invalid image data format');
      }

      // Create a data URL from the base64 image data
      const imageDataUrl = `data:image/png;base64,${item.image}`;
      
      // Store the image data for MoMM to use
      const imageForMoMM = {
        url: imageDataUrl,
        name: `ComfyUI Image - ${item.name || item.hash?.substring(0, 8) || 'Unknown'}`,
        hash: item.hash,
        prompt: item.prompt || '',
        fromComfyUI: true,
        originalItem: {
          hash: item.hash,
          name: item.name,
          prompt: item.prompt,
          timestamp: item.timestamp
        },
        timestamp: Date.now() // Add timestamp for debugging
      };

      console.log('📤 ComfyImageToolbar: Storing image data...', {
        name: imageForMoMM.name,
        promptLength: imageForMoMM.prompt.length,
        dataUrlLength: imageForMoMM.url.length
      });

      // Store in localStorage so MoMM can access it
      localStorage.setItem('momm_imported_image', JSON.stringify(imageForMoMM));
        
      // Verify storage
      const stored = localStorage.getItem('momm_imported_image');
      if (!stored) {
        throw new Error('Failed to store image data in localStorage');
      }
        
      console.log('✅ ComfyImageToolbar: Image data stored successfully');
        
        // Show a brief success message using a simple toast-like approach
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        toast.textContent = 'Image sent to MoMM! Redirecting...';
        document.body.appendChild(toast);
        
        // Remove the toast after a short delay and navigate
        setTimeout(() => {
          toast.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(toast)) {
              document.body.removeChild(toast);
            }
          }, 300);
          console.log('📤 ComfyImageToolbar: Navigating to MOMM...');
          navigate('/momm');
        }, 1000);
    } catch (err) {
      console.error('📤 ComfyImageToolbar: Error sending image to MoMM:', err);
      
      // Determine error message
      let errorMessage = 'Error sending image to MoMM';
      if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      // Show error toast
      const errorToast = document.createElement('div');
      errorToast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
      errorToast.textContent = errorMessage;
      document.body.appendChild(errorToast);
      
      setTimeout(() => {
        errorToast.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(errorToast)) {
            document.body.removeChild(errorToast);
          }
        }, 300);
      }, 3000); // Show error for longer
    }
  };

  // Create handler for tag image
  const handleTagClick = (e) => {
    e.stopPropagation();
    if (onTagImage) {
      onTagImage(item);
    }
  };

  // Create handler for describe image
  const handleDescribeClick = (e) => {
    e.stopPropagation();
    if (onDescribeImage) {
      onDescribeImage(item);
    }
  };

  return (
    <>
      {/* Main toolbar */}
      <div 
        className="w-full bg-dark-blue/90 backdrop-blur p-2 rounded-md shadow-md"
      >
        <div className="flex flex-wrap gap-2 justify-center">
          <button 
            title={`${localInCollection ? "Remove from Collection" : "Add to Collection"}`}
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={handleToggleCollection}
          >
            {localInCollection ?
              <CardInCollection alt={"In Collection Icon"} className="w-5 h-5" /> 
            :
              <CardAdd alt={"Add to Collection Icon"} className="w-5 h-5" />
            }
          </button>
          
          <button 
            title="Examine Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={handleZoom}
          >
            <CardZoom alt={"Zoom Icon"} className="w-5 h-5" />
          </button>
          
          
          <button 
            title="Copy Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={copyImageToClipboard}
          >
            <CardCopy alt={"Copy Icon"} className="w-5 h-5" />
          </button>
          
          <button 
            title="Save Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={(e) => {
              e.stopPropagation();
              if (onSaveImage) {
                onSaveImage(item, downloadName);
              }
            }}
          >
            <CardSave alt={"Save Icon"} className="w-5 h-5" />
          </button>
          
          <button 
            title="Use Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={handleUseImage}
          >
            <UploadIconWhite alt={"Use Image"} className="w-5 h-5" />
          </button>
          
          {/* Send to MoMM button - only show for images */}
          {item.data_type === 'image' && !isInMommPage && (
            <button 
              title="Send to MoMM for AI iteration"
              className="w-8 h-8 flex items-center justify-center hover:bg-purple-600/70 bg-purple-700/50 rounded transition"
              onClick={handleSendToMoMM}
            >
              <MOMMIcon />
            </button>
          )}
          
          
          <button 
            title="Tag Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={handleTagClick}
          >
            <CardTag alt={"Tag Icon"} className="w-5 h-5" />
          </button>
          
          <button 
            title="Describe Image"
            className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
            onClick={handleDescribeClick}
          >
            <CardImageDescribe alt={"Describe Icon"} className="w-5 h-5" />
          </button>
          
          {!isInMommPage && (
            <button 
              title="Delete Image"
              className="w-8 h-8 flex items-center justify-center hover:bg-dark-blue/50 rounded transition"
              onClick={handleDelete}
            >
              <CardRemove alt={"Remove Icon"} className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {showSaveDialog && (
          <div className="w-full flex flex-col gap-2 mt-2 bg-dark-blue/90 backdrop-blur p-2 rounded">
            <input 
              type="text" 
              className="w-full rounded text-dark-blue p-2 text-sm"
              onChange={(e) => setDownloadName(e.target.value)} 
              value={downloadName}
              onFocus={(e) => e.target.select()}
            />
            <div className="w-full flex flex-row justify-center gap-4">
              <button 
                className="bg-dark-blue/40 rounded px-4 py-2 hover:bg-dark-blue transition" 
                onClick={(e) => {
                  saveImage(e);
                  setShowSaveDialog(false);
                }}
              >
                Save
              </button>
              <button 
                className="bg-dark-blue/40 rounded px-4 py-2 hover:bg-dark-blue transition" 
                onClick={() => setShowSaveDialog(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
      </div>

      {/* Image Details Dialog */}
      {showDetailsDialog && (
        <div className="fixed inset-y-0 right-0 z-[9999] w-80 bg-[#1e1147] shadow-xl">
          <div className="flex justify-between items-center p-4">
            <h3 className="text-xl font-bold text-amber-500">Image Details:</h3>
            <button 
              className="text-white hover:text-amber-500 text-2xl"
              onClick={closeDetailsDialog}
            >
              ×
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <span className="font-medium text-amber-500">Prompt:</span> <span className="text-white">{item.prompt || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Negative Prompt:</span> <span className="text-white">{item.negative_prompt || ''}</span>
            </div>
            {!isInMommPage && (
              <div>
                <span className="font-medium text-amber-500">Seed:</span> <span className="text-white">{item.seed_value || (item.params && item.params.seed_value) || ''}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-amber-500">Steps:</span> <span className="text-white">{(item.params && item.params.num_inference_steps) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Guidance:</span> <span className="text-white">{(item.params && item.params.guidance_scale) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Model:</span> <span className="text-white">{item.model || (item.params && item.params.model) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Height x Width:</span> <span className="text-white">{item.height || (item.params && item.params.height) ? (item.height || (item.params && item.params.height)) + ' x ' + (item.width || (item.params && item.params.width)) : ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Scheduler:</span> <span className="text-white">{(item.params && item.params.scheduler) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Lora:</span> <span className="text-white">{(item.params && item.params.lora) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Lora Scale:</span> <span className="text-white">{(item.params && item.params.lora_scale) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Count:</span> <span className="text-white">{(item.params && item.params.count) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Image Batch Size:</span> <span className="text-white">{(item.params && item.params.image_batch_size) || ''}</span>
            </div>
            <div>
              <span className="font-medium text-amber-500">Control Net:</span> <span className="text-white">{(item.params && item.params.control_net) || ''}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComfyImageToolbar; 